import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronDown, Loader2, Plus, Trash2 } from 'lucide-react';
import type { ContentVocabularyItemDto } from '../../services/contentService';
import { LEITNER_CHANGED_EVENT, emitLeitnerChanged, leitnerService, LeitnerSourceKind } from '../../services/leitnerService';
import { WORD_POS_I18N } from '../../utils/wordPartOfSpeech';
import { useAppLocale } from '../../providers/LocaleProvider';
import { pickBlockTitle } from '../../utils/localizedContent';
import { LEARNER_UI_PERSIAN_STACK } from '../../constants/learnerPersianFonts';
import { useNotifications } from '../feedback/NotificationSystem';
import { CollapsibleFadePanel } from '../shared/CollapsibleFadePanel';
import { hasVocabCollapseMedia, VocabCollapseMedia } from '../shared/VocabCollapseMedia';
import { VocabPronounceButton } from '../shared/VocabPronounceButton';
import { useElementMinWidth } from '../../hooks/useElementMinWidth';

const CONFIRM_MS = 220;
const VOCAB_TWO_COL_MIN_PX = 768;

function VocabHeaderRow() {
  const { t } = useTranslation('leitner');
  return (
    <tr className="border-b border-gray-200 bg-gray-100 text-xs font-semibold text-gray-600 dark:border-gray-600 dark:bg-gray-900/80 dark:text-gray-300">
      <th scope="col" className="px-1.5 py-2 text-center font-semibold">
        <span className="sr-only">{t('field.audio')}</span>
      </th>
      <th scope="col" aria-hidden className="px-1.5 py-2" />
      <th scope="col" className="whitespace-nowrap px-1.5 py-2 text-center font-semibold">{t('field.persian')}</th>
      <th scope="col" dir="ltr" className="whitespace-nowrap px-1.5 py-2 text-center font-semibold">{t('field.phonetic')}</th>
      <th scope="col" dir="ltr" className="whitespace-nowrap px-1.5 py-2 text-center font-semibold">{t('field.english')}</th>
      <th scope="col" className="whitespace-nowrap px-1.5 py-2 text-center font-semibold">{t('field.action')}</th>
    </tr>
  );
}

interface VocabularyBlockDisplayProps {
  block: {
    id?: number;
    title?: string;
    titleFa?: string;
    vocabularyItems?: ContentVocabularyItemDto[];
  };
  sourceKind: 'lesson' | 'book';
  lessonId?: number;
  libraryBookId?: number;
  isHighlighted?: boolean;
  highlightClass?: string;
  blockDomId?: string;
  readOnly?: boolean;
}

export const VocabularyBlockDisplay: React.FC<VocabularyBlockDisplayProps> = ({
  block,
  sourceKind,
  lessonId,
  libraryBookId,
  highlightClass = '',
  blockDomId,
  readOnly = false
}) => {
  const { t } = useTranslation('leitner');
  const { showSuccess, showError, showInfo } = useNotifications();
  const { locale } = useAppLocale();
  const isFa = locale === 'fa';
  const items = useMemo(
    () => [...(block.vocabularyItems ?? [])].sort((a, b) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0)),
    [block.vocabularyItems]
  );
  const [boxIds, setBoxIds] = useState<Record<string, number>>({});
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const [confirmKey, setConfirmKey] = useState<string | null>(null);
  const [confirmClosing, setConfirmClosing] = useState(false);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [rootRef, desktopCols] = useElementMinWidth(VOCAB_TWO_COL_MIN_PX);
  const title = pickBlockTitle(block, locale);
  const vazirStyle: React.CSSProperties = { fontFamily: LEARNER_UI_PERSIAN_STACK };

  useEffect(() => {
    if (readOnly) return;
    const persians = items.map((i) => i.persian).filter(Boolean);
    if (persians.length === 0) return;
    const refresh = () => {
      void leitnerService.status(persians).then((rows) => {
        const next: Record<string, number> = {};
        for (const row of rows) {
          if (row.inBox && row.cardId) next[row.persian] = row.cardId;
        }
        setBoxIds(next);
      }).catch(() => undefined);
    };
    refresh();
    window.addEventListener(LEITNER_CHANGED_EVENT, refresh);
    return () => window.removeEventListener(LEITNER_CHANGED_EVENT, refresh);
  }, [items, readOnly]);

  const dismissConfirm = useCallback((entryKey: string) => {
    if (confirmClosing) return;
    setConfirmClosing(true);
    window.setTimeout(() => {
      setConfirmKey((current) => (current === entryKey ? null : current));
      setConfirmClosing(false);
    }, CONFIRM_MS);
  }, [confirmClosing]);

  useEffect(() => {
    if (!confirmKey) return undefined;
    const entryKey = confirmKey;
    const handlePointerDown = (event: PointerEvent) => {
      const root = document.querySelector(`[data-vocab-confirm-root="${entryKey}"]`);
      if (root?.contains(event.target as Node)) return;
      dismissConfirm(entryKey);
    };
    const timeoutId = window.setTimeout(() => {
      document.addEventListener('pointerdown', handlePointerDown, true);
    }, 0);
    return () => {
      window.clearTimeout(timeoutId);
      document.removeEventListener('pointerdown', handlePointerDown, true);
    };
  }, [confirmKey, dismissConfirm]);

  const toggleExpand = (key: string, canExpand: boolean) => {
    if (!canExpand) return;
    setExpandedKey((prev) => (prev === key ? null : key));
  };

  const addWord = async (item: ContentVocabularyItemDto) => {
    if (readOnly || busyKey) return;
    setBusyKey(item.entryKey);
    try {
      const result = await leitnerService.bulkAdd([
        {
          persian: item.persian,
          english: item.english,
          phonetic: item.phonetic,
          partOfSpeech: item.partOfSpeech,
          example: item.example,
          imageUrl: item.imageUrl,
          audioUrl: item.audioUrl,
          audioFileName: item.audioFileName,
          sourceKind: sourceKind === 'book' ? LeitnerSourceKind.Book : LeitnerSourceKind.Lesson,
          sourceLessonId: lessonId,
          sourceLibraryBookId: libraryBookId,
          sourceContentBlockId: sourceKind === 'lesson' ? block.id : undefined,
          sourceLibraryBookBlockId: sourceKind === 'book' ? block.id : undefined,
          sourceEntryKey: item.entryKey
        }
      ]);
      const cardId = result.cards?.[0]?.id;
      if (cardId) {
        setBoxIds((prev) => ({ ...prev, [item.persian]: cardId }));
      }
      setConfirmKey(null);
      setConfirmClosing(false);
      if (result.added > 0) {
        showSuccess(t('addedToBox', { count: result.added }));
        emitLeitnerChanged({ added: true });
      } else if (result.alreadyPresent > 0) {
        showInfo(t('alreadyInBox', { count: result.alreadyPresent }));
        emitLeitnerChanged();
      }
    } catch (err) {
      showError(err instanceof Error ? err.message : t('addFailed'));
    } finally {
      setBusyKey(null);
    }
  };

  const removeWord = async (item: ContentVocabularyItemDto, cardId: number) => {
    if (busyKey) return;
    setBusyKey(item.entryKey);
    try {
      await leitnerService.remove(cardId);
      setBoxIds((prev) => {
        const next = { ...prev };
        delete next[item.persian];
        return next;
      });
      setConfirmKey(null);
      setConfirmClosing(false);
      emitLeitnerChanged();
      showSuccess(t('removedFromBox'));
    } catch (err) {
      showError(err instanceof Error ? err.message : t('deleteFailed'));
    } finally {
      setBusyKey(null);
    }
  };

  if (items.length === 0) return null;

  return (
    <div
      ref={rootRef}
      id={blockDomId}
      style={vazirStyle}
      className={`rounded-lg border border-gray-200 bg-white p-2 shadow-[0_0_6px_-1px_rgba(0,0,0,0.12),0_4px_6px_-1px_rgba(0,0,0,0.1),0_2px_4px_-2px_rgba(0,0,0,0.1)] dark:border-gray-700 dark:bg-gray-800 ${highlightClass}`}
    >
      {title ? (
        <h3 className="mb-3 font-semibold text-gray-700 dark:text-gray-300">
          {title}
        </h3>
      ) : null}
      <div className={`grid items-start gap-x-3 ${desktopCols ? 'grid-cols-2' : 'grid-cols-1'}`}>
        {(desktopCols
          ? [items.filter((_, index) => index % 2 === 0), items.filter((_, index) => index % 2 === 1)]
          : [items]
        ).filter((column) => column.length > 0).map((column, columnIndex) => (
        <div key={columnIndex} className="overflow-hidden rounded-lg border border-gray-200 dark:border-gray-600">
        <table
          dir={isFa ? 'rtl' : 'ltr'}
          className="w-full table-auto border-collapse text-center"
        >
        <thead>
          <VocabHeaderRow />
        </thead>
        <tbody>
        {column.map((item) => {
          const cardId = boxIds[item.persian];
          const inBox = cardId != null;
          const pos = WORD_POS_I18N[item.partOfSpeech ?? 5];
          const canExpand = hasVocabCollapseMedia(item.imageUrl, item.example);
          const isExpanded = expandedKey === item.entryKey;
          const isBusy = busyKey === item.entryKey;
          const isConfirmOpen = confirmKey === item.entryKey;
          const posLabel = locale === 'fa' ? pos.fa : pos.en;
          return (
            <React.Fragment key={item.entryKey}>
            <tr
              role={canExpand ? 'button' : undefined}
              tabIndex={canExpand ? 0 : undefined}
              onClick={() => toggleExpand(item.entryKey, canExpand)}
              onKeyDown={(e) => {
                if (!canExpand) return;
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  toggleExpand(item.entryKey, canExpand);
                }
              }}
              className={`${canExpand ? 'cursor-pointer' : 'border-b border-gray-200 dark:border-gray-600'}`}
            >
                <td className="px-1 py-1">
                <VocabPronounceButton url={item.audioUrl} ariaLabel={item.persian} size="sm" />
                </td>
                <td className="whitespace-nowrap px-1 py-1 text-center">
                <span className="inline-flex w-max rounded-full border border-gray-200 px-1 py-0 text-[8px] font-medium leading-3 text-gray-600 dark:border-gray-600 dark:text-gray-300">
                  {posLabel}
                </span>
                </td>
                <td dir="rtl" className="whitespace-nowrap px-1 py-1 text-center text-[13px] font-semibold text-gray-900 dark:text-gray-50">
                  {item.persian}
                </td>
                <td dir="ltr" className="whitespace-nowrap px-1 py-1 text-center text-xs text-gray-500 dark:text-gray-400">
                  {item.phonetic || ''}
                </td>
                <td dir="ltr" className="whitespace-nowrap px-1 py-1 text-center text-xs text-gray-600 dark:text-gray-300">
                  {item.english || ''}
                </td>
                <td className="whitespace-nowrap px-1 py-1 text-center">
                <div className="inline-flex w-max items-center justify-center gap-0">
                {readOnly ? (
                  <span className="h-7 w-7" aria-hidden />
                ) : (
                  <div className="relative flex items-center">
                    {isConfirmOpen && (
                      <div
                        data-vocab-confirm-root={item.entryKey}
                        dir={isFa ? 'rtl' : 'ltr'}
                        className={`absolute top-1/2 z-10 flex -translate-y-1/2 items-center gap-2 rounded-lg border border-gray-400/35 bg-white/20 px-3 py-0.5 shadow-lg backdrop-blur-[2px] dark:border-gray-500/35 dark:bg-gray-950/20 ${
                          confirmClosing ? 'animate-favorite-confirm-out' : 'animate-favorite-confirm-in'
                        } ${isFa ? 'left-full ml-1.5' : 'right-full mr-1.5'}`}
                        onClick={(e) => e.stopPropagation()}
                        role="group"
                        aria-label={inBox ? t('confirmRemoveFromBox') : t('confirmAdd')}
                      >
                        <span className="whitespace-nowrap text-xs font-semibold text-gray-800 drop-shadow-sm dark:text-gray-100">
                          {inBox ? t('confirmRemoveFromBox') : t('confirmAdd')}
                        </span>
                        <button
                          type="button"
                          disabled={isBusy || confirmClosing}
                          onClick={(e) => {
                            e.stopPropagation();
                            dismissConfirm(item.entryKey);
                          }}
                          className="min-w-[2.25rem] rounded-md px-2.5 py-1 text-xs font-medium text-gray-800 hover:bg-black/10 disabled:opacity-50 dark:text-gray-100 dark:hover:bg-white/10"
                        >
                          {t('confirmRemoveNo')}
                        </button>
                        <button
                          type="button"
                          disabled={isBusy || confirmClosing}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (inBox && cardId) void removeWord(item, cardId);
                            else void addWord(item);
                          }}
                          className="min-w-[2.25rem] rounded-md bg-rose-500/25 px-2.5 py-1 text-xs font-medium text-rose-800 hover:bg-rose-500/35 disabled:opacity-50 dark:text-rose-200 dark:hover:bg-rose-500/30"
                        >
                          {isBusy ? (
                            <Loader2 className="mx-auto h-4 w-4 animate-spin" aria-hidden />
                          ) : (
                            t('confirmRemoveYes')
                          )}
                        </button>
                      </div>
                    )}
                    <button
                      type="button"
                      disabled={isBusy || isConfirmOpen}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (isConfirmOpen) return;
                        setConfirmClosing(false);
                        setConfirmKey(item.entryKey);
                      }}
                      className="relative z-20 shrink-0 rounded-lg p-1 text-rose-600 transition-colors hover:bg-rose-100 disabled:opacity-100 dark:text-rose-300 dark:hover:bg-rose-950/50"
                      title={inBox ? t('delete') : t('addChip')}
                      aria-label={inBox ? t('delete') : t('addChip')}
                    >
                      {isBusy && !isConfirmOpen ? (
                        <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                      ) : inBox ? (
                        <Trash2 className="h-4 w-4" />
                      ) : (
                        <Plus className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                )}
                {canExpand ? (
                  <ChevronDown
                    className={`h-3.5 w-3.5 shrink-0 text-rose-400 transition-transform duration-200 ${
                      isExpanded ? 'rotate-180' : ''
                    }`}
                    aria-hidden
                  />
                ) : (
                  <span className="h-3.5 w-3.5" aria-hidden />
                )}
                </div>
                </td>
            </tr>
              {canExpand ? (
                <tr>
                  <td colSpan={6} className={`border-b border-gray-200 bg-white dark:border-gray-600 dark:bg-gray-800 ${isExpanded ? 'p-1.5' : 'p-0'}`}>
                <CollapsibleFadePanel expanded={isExpanded}>
                  <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-1.5 dark:border-gray-600 dark:bg-gray-900/50">
                    <VocabCollapseMedia
                      imageUrl={item.imageUrl}
                      example={item.example}
                      exampleLabel={item.example ? t('example') : undefined}
                      imagePlacement={isFa ? 'left' : 'right'}
                      exampleDir={isFa ? 'rtl' : 'ltr'}
                    />
                  </div>
                </CollapsibleFadePanel>
                  </td>
                </tr>
              ) : null}
            </React.Fragment>
          );
        })}
        </tbody>
        </table>
        </div>
        ))}
      </div>
    </div>
  );
};
